import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ButtonComponent } from "@components/button/button.component";
import { CheckboxComponent } from "@components/checkbox/checkbox.component";
import { InputComponent } from "@components/input/input.component";
import { MultiSelectorComponent } from "@components/multi-selector/multi-selector.component";
import { SimpleSelectorComponent } from "@components/simple-selector/simple-selector.component";
import { SubmitButtonComponent } from "@components/submit-button/submit-button.component";
import { TreeSelectorComponent } from "@components/tree-selector/tree-selector.component";
import _ from "lodash";
import { Observable, Subject, forkJoin, of } from "rxjs";
import { map, shareReplay, takeUntil } from "rxjs/operators";
import { IRadiobuttonItem } from "~app/common/components/radiobuttongroup/radiobutton/IRadiobuttonItem";
import { SelectionItem } from "~app/common/components/tree-selector/selection-item";
import { Avverkningsgrupper, RaskRoller, SkogsvardsStatus, VirkeskopAffarsformer } from "~app/common/models/constants";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { FeatureService } from "~app/common/services/feature.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { VikariehanteringService } from "~app/features/administration/vikariehantering/vikariehantering.service";
import { ISearchVirkeskopAnbudKontraktRequest } from "~app/features/virkeskop/models/kontrakt-search-model";
import { IAtgardstypWithGroup } from "../../models/atgardstyp-with-group-dto";
import {
    ANSKAFFNINGSFORMERSOKNING,
    ATGARD_PLANERINGSSTATUS,
    KONTRAKTSTATUSAR,
    KONTRAKTSTYPER,
    VACS_KONTRAKTSTATUSAR
} from "../../models/kontrakt-detail-model";
import { SkogsvardService } from "../../services/skogsvard.service";
import { VirkeskopService } from "../../services/virkeskop.service";
import { VirkesleverantorService } from "../../virkesleverantor/services/virkesleverantor.service";
import { AnsvarigAnvandareModel, IVKSearchFilter } from "../models/kontrakt-search-filter-model";
import { AnbudKontraktSearchService } from "../services/anbud-kontrakt-search.service";
import { KontraktSaveFilterComponent } from "./kontrakt-search-mina-filter/kontrakt-save-filter/kontrakt-save-filter.component";
import { KontraktSearchMinaFilterComponent } from "./kontrakt-search-mina-filter/kontrakt-search-mina-filter.component";

// tree-selector tycks inte fungera så bra ihop med form-binder, så åtgärdstyp lagras utanför modellen
type FormModel = Omit<IVKSearchFilter, "atgardstyp" | "skogsvardstyp">;

const buildAtgardstypTree = (atgardstyper: IAtgardstypWithGroup[]): SelectionItem[] => {
    if (!atgardstyper) {
        return [];
    }

    const mapAtgardstypToSelectionItem = ({ namn, kortnamn }: IAtgardstypWithGroup): SelectionItem => ({
        text: namn,
        value: kortnamn
    });

    const groups = _.chain(atgardstyper)
        .map("grupp")
        .uniq()
        .remove((x) => !IGNORED_GROUPS.includes(x))
        .sort().value();
    const tree: SelectionItem[] = groups.map((grupp) => {
        let children = atgardstyper.filter((x) => x.grupp === grupp);
        children = _.sortBy(children, "namn");

        // i det fall att gruppen bara innehåller en typ står den bland grupperna
        if (children.length === 1) {
            const [atgTyp] = children;
            return mapAtgardstypToSelectionItem(atgTyp);
        }

        const childItems = children.map(mapAtgardstypToSelectionItem);
        return {
            text: grupp,
            value: grupp,
            children: childItems
        };
    });

    return tree;
};

const DEFAULT_MODEL: FormModel = {
    ansvarigVirkeskopare: [],
    ansvarigFaltplanerare: [],
    virkesleverantor: [],
    arbetsorderbeteckning: "",
    sok: "",
    affarsform: [],
    anskaffningsform: "",
    kontraktstyp: undefined,
    kontraktstatus: [],
    planeringsstatus: [],
    vacsStatus: [],
    skogsvardsStatus: [],
    sluttransporterat: false
};

// åtgärdsgrupper som inte finns i VK
const IGNORED_GROUPS = [Avverkningsgrupper.HyggesfriAvverkning];

@Component({
    selector: "rask-sok-anbud-kontrakt",
    templateUrl: "./sok-anbud-kontrakt.component.html",
    styleUrls: ["./sok-anbud-kontrakt.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        KontraktSearchMinaFilterComponent,
        FormsModule,
        ReactiveFormsModule,
        InputComponent,
        MultiSelectorComponent,
        SubmitButtonComponent,
        ButtonComponent,
        KontraktSaveFilterComponent,
        SimpleSelectorComponent,
        TreeSelectorComponent,
        CheckboxComponent
    ]
})
export class SokAnbudKontraktComponent implements OnInit, OnDestroy {
    protected activetypes: IRadiobuttonItem[] = [
        {
            key: "true",
            value: "Ja"
        },
        {
            key: "false",
            value: "Nej"
        },
        {
            key: "",
            value: "Alla"
        }
    ];
    protected affarsformer: SelectionItem[] = VirkeskopAffarsformer.AllaWithShortType.map(({ key, value }) => ({
        text: `${key}`,
        value
    }));
    protected anskaffingsformer = ANSKAFFNINGSFORMERSOKNING;
    protected atgardPlaneringstatusar: SelectionItem[] = ATGARD_PLANERINGSSTATUS.map((value) => ({
        text: value,
        value
    }));
    protected atgardTypsRubrik = "Åtgärdstyp produktion";
    protected atgardstypItems = [] as SelectionItem[];
    protected form: UntypedFormGroup;
    protected kontrakstyper = KONTRAKTSTYPER;
    protected kontraktstatusar: SelectionItem[] = KONTRAKTSTATUSAR.map((value) => ({
        text: value,
        value
    }));
    protected model = _.cloneDeep(DEFAULT_MODEL);
    protected skogsvardsItems = [] as SelectionItem[];
    protected skogsvardsStatusItems: Observable<SelectionItem[]> = of(SkogsvardsStatus.alla.map(x => ({
        value: x,
        text: x,
        selected: false
    })));
    protected skogsvardsStautsSelection: SelectionItem[];
    protected vacsKontraktstatusar: SelectionItem[] = VACS_KONTRAKTSTATUSAR.map(({ status, statusCode }) => ({
        text: status,
        value: statusCode
    }));
    protected virkeskopareSokBehorighet: boolean;

    protected disabledAnsvarigVirkeskopare$: Observable<AnsvarigAnvandareModel[]>;

    private readonly anbudKontraktSearchService = inject(AnbudKontraktSearchService);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly featureService = inject(FeatureService);
    private readonly formBinderService = inject(FormBinderService);
    private readonly skogsvardService = inject(SkogsvardService);
    private readonly unsubscribe$ = new Subject<void>();
    private readonly virkeskopService = inject(VirkeskopService);
    private readonly virkesleverantorService = inject(VirkesleverantorService);
    private readonly vikariehanteringService = inject(VikariehanteringService);

    private get atgardstyp(): string[] {
        return _.chain(this.atgardstypItems)
            .map((x) => x.children ?? x)
            .flatten()
            .filter((x) => !x.children && x.selected)
            .map((x) => x.value)
            .value();
    }

    private get skogsvardstyp(): string[] {
        return _.chain(this.skogsvardsItems)
            .map((x) => x.children ?? x)
            .flatten()
            .filter((x) => !x.children && x.selected)
            .map((x) => x.value)
            .value();
    }

    ngOnDestroy(): void {
        this.anbudKontraktSearchService.saveRecentScrollPosition();
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
    }

    ngOnInit(): void {
        this.virkeskopareSokBehorighet = this.authenticationService.hasRoles(`${RaskRoller.role.Kopleda},${RaskRoller.role.Kopplanera},${RaskRoller.role.VirkeskopTitta}`, false);

        this.form = this.formBinderService.CreateGroup(this.model, undefined, undefined, false);

        this.form.valueChanges.subscribe(x => {
            this.anbudKontraktSearchService.searchCriteriaChanged(this.getFilterFromForm());
        });

        this.anbudKontraktSearchService.searchFilterTemplate$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe(filter => {
                this.applyFilter(filter, false);
            });
        this.initSearchFilter();

        this.disabledAnsvarigVirkeskopare$ = this.getVikarieForAnvandare().pipe(
            shareReplay(1)
        );
    }

    protected affarsformFormatter = (obj: any) => obj.key;

    protected atgardsTyperChanged() {
        this.anbudKontraktSearchService.searchCriteriaChanged(this.getFilterFromForm());
    }

    protected onClear() {
        _.assign(this.model, _.cloneDeep(DEFAULT_MODEL));
        this.clearAtgardstyp(this.atgardstypItems);
        this.clearAtgardstyp(this.skogsvardsItems);
        this.skogsvardsStautsSelection = [];
        this.modelChanged();
    }

    protected onSearch() {
        const aoBeteckning = this.model.arbetsorderbeteckning;
        if (aoBeteckning !== "") {
            this.searchForAnbudKontraktAO(aoBeteckning);
            return;
        }

        const request = this.getRequestFromForm();
        const observable = this.virkeskopService.searchForAnbudKontrakt(request);
        this.anbudKontraktSearchService.search(observable);
    }

    protected onSkogsvardsStatusChanged(items: SelectionItem[]) {
        this.skogsvardsStautsSelection = items;
        this.anbudKontraktSearchService.searchCriteriaChanged(this.getFilterFromForm());
    }

    protected getAnvandareTypeAhead = (input: string) => this.virkesleverantorService.getAnvandareTypeAhead(input);

    protected skogsvardViewFormatterFn = (item: SelectionItem): string => item ? `${item.text}` : "";

    private applyDefaultSearchFilter() {
        const defaultAnsvarig = this.authenticationService.getUserData();

        this.model.ansvarigVirkeskopare = [
            {
                kod: defaultAnsvarig.userName,
                namn: defaultAnsvarig.fullUserName
            }
        ];

        this.modelChanged();
        this.onSearch();
    }

    private applyFilter(filter: IVKSearchFilter, performSearch: boolean) {
        // Gör så att filtret får defaultvärden från defaultmodellen först, annars så rensas inte nya värden
        const filterToApply = {} as IVKSearchFilter;
        _.assign(filterToApply, _.cloneDeep(DEFAULT_MODEL));
        filterToApply.atgardstyp = [];
        filterToApply.skogsvardstyp = [];
        _.assign(filterToApply, _.cloneDeep(filter));

        // Hanterar de fall där det är ett gammalt filter från när det endast gick att söka på en ansvarig virkesköpare
        const ansvarigVkOld = (filterToApply as any)?.ansvarigVirkeskopare;
        if (filterToApply && ObjectUtils.hasValue(ansvarigVkOld) && !Array.isArray(ansvarigVkOld)) {
            filterToApply.ansvarigVirkeskopare = [ansvarigVkOld];
        }
        if (filterToApply && ObjectUtils.hasValue((filterToApply as any).ansvarigvirkeskopareNamn)) {
            delete (filterToApply as any).ansvarigvirkeskopare;
            delete (filterToApply as any).ansvarigvirkeskopareNamn;
            delete (filterToApply as any).sokMinaPagaende;
        }
        _.assign(this.model, filterToApply);
        this.applyFilterToAtgardsTyp(filterToApply.atgardstyp, this.atgardstypItems);
        this.applyFilterToAtgardsTyp(filterToApply.skogsvardstyp, this.skogsvardsItems);
        this.applyFilterToSkogsvardsStatus(filterToApply.skogsvardsStatus);
        this.modelChanged();

        if (performSearch) {
            this.onSearch();
        }
    }

    private applyFilterToAtgardsTyp(filterAtgardsTyper: string[], typItems: SelectionItem[]) {
        typItems.forEach(grupp => {
            grupp.children?.forEach(typ => {
                typ.selected = filterAtgardsTyper.includes(typ.value);
            });
            if (!grupp.children) {
                // Specialhantering av Grot och Leveransvirke (som inte har några children i detta sammanhang)
                grupp.selected = filterAtgardsTyper.includes(grupp.value);
            } else {
                grupp.selected = grupp.children.every(x => x.selected);
                grupp.partialSelected = (!grupp.selected && grupp.children.some(x => x.selected));
            }
        });
    }

    private applyFilterToSkogsvardsStatus(filterAtgardsTyper: string[]) {
        this.skogsvardsStautsSelection = filterAtgardsTyper.map(x => ({
            value: x,
            text: x,
            selected: true
        }));
    }

    private clearAtgardstyp(typItems: SelectionItem[]) {
        const items = _.chain(typItems)
            .map((x) => [
                x,
                ...(x.children ?? [])
            ])
            .flatten()
            .value();

        items.forEach((x) => x.selected = false);
    }

    private getAtgardstyper() {
        return this.virkeskopService.getAtgardsTyperWithGroups();
    }

    private getFilterFromForm(): IVKSearchFilter {
        return {
            ...this.model,
            atgardstyp: this.atgardstyp,
            skogsvardstyp: this.skogsvardstyp,
            skogsvardsStatus: this.skogsvardsStautsSelection?.map(x => x.text)
        };
    }

    private getRequestFromForm(): ISearchVirkeskopAnbudKontraktRequest {
        const {
            model, atgardstyp, skogsvardstyp
        } = this;
        const avk = model.ansvarigVirkeskopare?.map(x => x.kod) ?? [];
        const avfp = model.ansvarigFaltplanerare.map(x => x.kod);
        return {
            ...model,
            atgardstyp,
            skogsvardstyp,
            skogsvardsStatus: this.skogsvardsStautsSelection?.map(x => x.text) ?? [],
            kontraktstyp: model.kontraktstyp ?? "",
            anskaffningsform: model.anskaffningsform ?? "",
            ansvarigVirkeskopare: avk,
            ansvarigFaltplanerare: avfp,
            ansvarigSaljare: [],
            sluttransporterat: model.sluttransporterat ?? false,
            virkesleverantor: []
        };
    }

    private getSkogsvardstyper() {
        return this.skogsvardService.getSkogsvardsTyperWithGroups();
    }

    private initSearchFilter() {
        forkJoin([
            this.getAtgardstyper(),
            this.getSkogsvardstyper(),
            this.anbudKontraktSearchService.getRecentFilter()
        ]).subscribe(([
            atgardstyperDto,
            skogsvardstyperDto,
            filter
        ]) => {
            this.atgardstypItems = buildAtgardstypTree(atgardstyperDto);
            this.skogsvardsItems = buildAtgardstypTree(skogsvardstyperDto);
            if (filter) {
                this.applyFilter(filter, true);
            } else {
                this.applyDefaultSearchFilter();
            }
        });
    }

    private getVikarieForAnvandare(): Observable<AnsvarigAnvandareModel[]> {
        const { userName, fullUserName } = this.authenticationService.getUserData();

        return this.vikariehanteringService.getVirkeskopare({
            vikarieUsername: userName
        }).pipe(
            map(res => {
                const virkeskopare: AnsvarigAnvandareModel[] = res.map(v => ({
                    kod: v.anvandare.userName,
                    namn: v.anvandare.namn
                }));

                return [
                    {
                        kod: userName,
                        namn: fullUserName
                    },
                    ...virkeskopare
                ];
            })
        );
    }

    private modelChanged() {
        const model = _.cloneDeep(this.model);
        this.formBinderService.updateFromModel(this.form, model, true);
    }

    private searchForAnbudKontraktAO(beteckning: string) {
        _.assign(this.model, _.cloneDeep(DEFAULT_MODEL)); // töm övriga fält
        this.clearAtgardstyp(this.atgardstypItems);
        this.clearAtgardstyp(this.skogsvardsItems);
        this.model.arbetsorderbeteckning = beteckning;
        this.modelChanged();

        this.anbudKontraktSearchService.search(this.virkeskopService.findAnbudKontraktByArbetsorder(beteckning));
    }
}
